// Optional ad-hoc invoice line-item editor (docs/24 §14). nsofar renders the canonical invoice;
// these rows are the coordinator's breakdown. Amounts are entered in MAJOR units (e.g. ₹) and
// converted to minor units on submit. The line total becomes the invoice amount when itemised.

import type { JSX } from "react";

export interface LineItemRow { description: string; qty: string; unit: string; }
export interface ComputedLine { description: string; qty: number; unit_amount_minor: number; amount_minor: number; }

export const EMPTY_ROW: LineItemRow = { description: "", qty: "1", unit: "" };

// Converts UI rows to the wire shape (minor units), dropping incomplete rows; returns the total.
export function computeLineItems(rows: LineItemRow[]): { lineItems: ComputedLine[]; totalMinor: number } {
  const lineItems = rows
    .filter((r) => r.description.trim() !== "" && Number(r.qty) > 0 && r.unit.trim() !== "" && Number(r.unit) >= 0)
    .map((r) => {
      const qty = Math.floor(Number(r.qty));
      const unit_amount_minor = Math.round(Number(r.unit) * 100);
      return { description: r.description.trim(), qty, unit_amount_minor, amount_minor: qty * unit_amount_minor };
    });
  return { lineItems, totalMinor: lineItems.reduce((s, li) => s + li.amount_minor, 0) };
}

const INP = "rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500";

function Row({ row, onChange, onRemove }: { row: LineItemRow; onChange: (r: LineItemRow) => void; onRemove: () => void }): JSX.Element {
  return (
    <div className="flex gap-2 items-center">
      <input aria-label="Item description" placeholder="Item" value={row.description}
        onChange={(e) => onChange({ ...row, description: e.target.value })} className={`${INP} flex-1`} />
      <input aria-label="Quantity" type="number" min="1" placeholder="Qty" value={row.qty}
        onChange={(e) => onChange({ ...row, qty: e.target.value })} className={`${INP} w-16`} />
      <input aria-label="Unit amount" type="number" min="0" step="0.01" placeholder="Unit" value={row.unit}
        onChange={(e) => onChange({ ...row, unit: e.target.value })} className={`${INP} w-24`} />
      <button type="button" onClick={onRemove} aria-label="Remove line"
        className="text-slate-400 hover:text-red-600 text-lg leading-none px-1">×</button>
    </div>
  );
}

export function LineItemsEditor({ rows, onChange }: { rows: LineItemRow[]; onChange: (r: LineItemRow[]) => void }): JSX.Element {
  const update = (i: number, r: LineItemRow): void => onChange(rows.map((x, j) => (j === i ? r : x)));
  const remove = (i: number): void => onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="block text-sm font-medium text-slate-700">Line items (optional)</span>
        <button type="button" onClick={() => onChange([...rows, { ...EMPTY_ROW }])}
          className="text-xs text-navy-700 font-medium hover:underline">+ Add line</button>
      </div>
      {rows.map((row, i) => (
        <Row key={i} row={row} onChange={(r) => update(i, r)} onRemove={() => remove(i)} />
      ))}
    </div>
  );
}
